import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { Capacitor } from '@capacitor/core';
import {
    initPurchases,
    presentPaywall,
    presentCustomerCenter,
    checkProEntitlement,
    restorePurchases,
} from '../lib/purchases';

const benefits = [
    { icon: '♾️', title: 'Unlimited Scans', description: 'Scan as many items as you want, no monthly limits' },
    { icon: '⚡', title: 'Priority Analysis', description: 'Faster AI processing for instant results' },
    { icon: '📊', title: 'Detailed Stats', description: 'Track your environmental impact over time' },
    { icon: '🏆', title: 'Exclusive Badges', description: 'Unlock special achievements and badges' },
    { icon: '🌍', title: 'Community Impact', description: 'See how you stack up against other recyclers' },
    { icon: '💚', title: 'Support the Planet', description: 'Help fund recycling education initiatives' },
];

export default function UpgradePage() {
    const { user, isPro, refreshUserData } = useAuth();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    const [loading, setLoading] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [localIsPro, setLocalIsPro] = useState(isPro);
    const [initialized, setInitialized] = useState(false);

    // Initialize RevenueCat on iOS when user is authenticated
    useEffect(() => {
        async function init() {
            if (!isNative || !user) return;

            try {
                await initPurchases(user.uid);
                setInitialized(true);

                // Check entitlement status
                const proActive = await checkProEntitlement();
                setLocalIsPro(proActive);
            } catch (err) {
                console.error('Failed to init purchases:', err);
                setError('Failed to initialize purchase system');
            }
        }
        init();
    }, [isNative, user]);

    // Sync local Pro status with auth context
    useEffect(() => {
        setLocalIsPro(isPro);
    }, [isPro]);

    async function handleUpgrade() {
        if (!user) {
            navigate('/auth', { state: { returnTo: '/upgrade' } });
            return;
        }

        if (!initialized) {
            setError('Purchase system not ready. Please try again.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Present the RevenueCat paywall
            const success = await presentPaywall();

            if (success) {
                setLocalIsPro(true);
                // Refresh user data to update entitlements from Firestore
                await refreshUserData();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Purchase failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function handleRestore() {
        if (!initialized) {
            setError('Purchase system not ready. Please try again.');
            return;
        }

        setRestoring(true);
        setError(null);

        try {
            const { success, isPro: nowPro } = await restorePurchases();

            if (success) {
                if (nowPro) {
                    setLocalIsPro(true);
                    await refreshUserData();
                } else {
                    setError('No previous purchases found to restore.');
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Restore failed. Please try again.');
        } finally {
            setRestoring(false);
        }
    }

    async function handleManageSubscription() {
        try {
            await presentCustomerCenter();
        } catch (err) {
            console.error('Failed to open customer center:', err);
        }
    }

    // If already Pro, show success state with manage option
    if (localIsPro) {
        return (
            <div className="min-h-screen bg-primary-50">
                <Header />
                <main className="max-w-lg mx-auto px-4 py-6 safe-bottom text-center">
                    <div className="text-6xl mb-4">🎉</div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">You're Pro!</h1>
                    <p className="text-gray-600 mb-6">Enjoy unlimited scans and all Pro features.</p>

                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('/')}
                            className="w-full btn-primary"
                        >
                            Start Scanning
                        </button>

                        {isNative && (
                            <button
                                onClick={handleManageSubscription}
                                className="w-full text-primary-600 text-sm hover:text-primary-700"
                            >
                                Manage Subscription
                            </button>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary-50">
            <Header />

            <main className="max-w-lg mx-auto px-4 py-6 safe-bottom">
                {/* Hero */}
                <div className="text-center mb-8">
                    <div className="text-6xl mb-4">🌟</div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Go Pro!</h1>
                    <p className="text-gray-600">Unlock the full power of BinIt</p>
                </div>

                {/* Price Card */}
                <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white text-center mb-6">
                    <div className="text-sm uppercase tracking-wide mb-1">Pro Plan</div>
                    <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold">$4.99</span>
                        <span className="text-primary-100">/month</span>
                    </div>
                    <p className="text-primary-100 text-sm mt-2">
                        Monthly • Yearly • Lifetime options available
                    </p>
                </div>

                {/* Benefits */}
                <div className="space-y-3 mb-8">
                    {benefits.map((benefit, index) => (
                        <div key={index} className="card flex items-start gap-3">
                            <span className="text-2xl">{benefit.icon}</span>
                            <div>
                                <h3 className="font-semibold text-gray-900">{benefit.title}</h3>
                                <p className="text-sm text-gray-500">{benefit.description}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm text-center">
                        {error}
                    </div>
                )}

                {/* Platform-specific CTA */}
                {isNative ? (
                    // iOS: RevenueCat paywall
                    <div className="space-y-3">
                        <button
                            onClick={handleUpgrade}
                            disabled={loading || !user || !initialized}
                            className="w-full btn-primary text-lg py-4 disabled:opacity-50"
                        >
                            {loading ? 'Loading...' : !user ? 'Sign in to Subscribe' : 'View Plans & Subscribe'}
                        </button>
                        <button
                            onClick={handleRestore}
                            disabled={restoring || !initialized}
                            className="w-full text-primary-600 text-sm hover:text-primary-700 disabled:opacity-50"
                        >
                            {restoring ? 'Restoring...' : 'Restore Purchases'}
                        </button>
                    </div>
                ) : (
                    // Web: Explain iOS-only subscription
                    <div className="card bg-blue-50 border border-blue-200 text-center">
                        <div className="text-3xl mb-2">📱</div>
                        <h3 className="font-semibold text-gray-900 mb-1">Available on iOS</h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Pro subscriptions are available exclusively through the BinIt iOS app.
                        </p>
                        <p className="text-xs text-gray-500">
                            Download BinIt from the App Store to subscribe and unlock all Pro features.
                        </p>
                    </div>
                )}

                <button
                    onClick={() => navigate('/')}
                    className="w-full text-gray-500 text-sm hover:text-gray-700 mt-4"
                >
                    Maybe later
                </button>

                {/* Trust badges */}
                <div className="flex justify-center gap-6 mt-8 text-gray-400 text-xs">
                    {isNative && <span>🔒 Secure Apple Pay</span>}
                    <span>↩️ Cancel anytime</span>
                </div>
            </main>
        </div>
    );
}
